import { useMemo, useState } from "react";
import {
  disableFoodBranchLocatorSource,
  enableFoodBranchLocatorSource,
  fetchFoodBranchLocatorSourceEnablementStatus,
  fetchFoodBranchLocatorSourceIngestionReadiness,
  runFoodBranchLocatorSourceIngestion,
} from "../../../../api/foodApi";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { useAuth } from "../../../../auth/useAuth";
import { foodSourceGroupLabel } from "../../../../constants/foodSourceConstants";
import FoodSourceControlsGrid from "../dataSources/FoodSourceControlsGrid";
import {
  BRANCH_LOCATOR_IMPORTS_HREF,
  getBranchLocatorRunResultMessage,
  parseBranchLocatorApiError,
} from "./foodBranchLocatorSourceControlsHelpers";

function normalizeHealth(source) {
  if (!source.isActive) {
    return "disabled";
  }

  const status = String(source.healthStatus || source.computedHealthStatus || "").toLowerCase();
  if (status === "running") return "running";
  if (status === "failing" || status === "failed") return "failed";
  if (status === "stale" || status === "warning") return "warning";
  if (status === "healthy") return "healthy";
  if (status === "planned") return "never_run";
  if (status === "ready_for_file_import") return "never_run";
  if (status === "discovery_pending" || status === "not_configured") return "never_run";
  return status || "never_run";
}

const FoodBranchLocatorSourceControlsTable = ({
  sources,
  filters,
  onRefresh,
  showToast,
  onSourcePatch,
  hideEmpty = false,
}) => {
  const [busyCode, setBusyCode] = useState(null);
  const [runningCode, setRunningCode] = useState(null);
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageSources =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const filteredSources = useMemo(() => {
    let list = [...(sources || [])];

    if (filters.search) {
      const query = filters.search.toLowerCase();
      list = list.filter(
        (source) =>
          source.sourceName?.toLowerCase().includes(query) ||
          source.sourceCode?.toLowerCase().includes(query) ||
          foodSourceGroupLabel(source.sourceGroup)?.toLowerCase().includes(query)
      );
    }

    if (filters.enabledFilter === "enabled") {
      list = list.filter((source) => source.isActive);
    } else if (filters.enabledFilter === "disabled") {
      list = list.filter((source) => !source.isActive);
    }

    if (filters.statusFilter && filters.statusFilter !== "all") {
      list = list.filter((source) => normalizeHealth(source) === filters.statusFilter);
    }

    return list.sort((a, b) => a.sourceName.localeCompare(b.sourceName));
  }, [sources, filters]);

  if (hideEmpty && !filteredSources.length) {
    return null;
  }

  const patchSource = (sourceCode, patch) => {
    onSourcePatch?.(sourceCode, patch);
  };

  const handleToggleActive = async (source) => {
    if (!canManageSources) return;

    const nextActive = !source.isActive;
    setBusyCode(source.sourceCode);

    try {
      if (nextActive) {
        const enablement = await fetchFoodBranchLocatorSourceEnablementStatus(source.sourceCode);
        if (!enablement.canEnable) {
          showToast(
            enablement.blockingReasons?.[0] || `${source.sourceName} cannot be enabled yet.`,
            "error"
          );
          return;
        }

        const confirmed = window.confirm(
          `Enable ${source.sourceName} for branch locator file import?`
        );
        if (!confirmed) return;

        const result = await enableFoodBranchLocatorSource(source.sourceCode, {
          confirm: true,
          enabledBy: "admin",
          notes: "Enabled from Source Controls.",
        });
        patchSource(source.sourceCode, {
          isActive: true,
          healthStatus: "ready_for_file_import",
          computedHealthStatus: "never_run",
          activationNote: null,
        });
        showToast(result.message || `${source.sourceName} enabled.`, "success");
      } else {
        const reason = window.prompt(
          `Reason for disabling ${source.sourceName}:`,
          "Disabled from Source Controls."
        );
        if (!reason?.trim()) return;

        const result = await disableFoodBranchLocatorSource(source.sourceCode, {
          confirm: true,
          disabledBy: "admin",
          reason: reason.trim(),
        });
        patchSource(source.sourceCode, {
          isActive: false,
          healthStatus: "not_configured",
          computedHealthStatus: "planned",
        });
        showToast(result.message || `${source.sourceName} disabled.`, "success");
      }

      onRefresh?.({ silent: true });
    } catch (error) {
      showToast(parseBranchLocatorApiError(error), "error");
    } finally {
      setBusyCode(null);
    }
  };

  const handleRunNow = async (source) => {
    if (!canManageSources) return;

    setBusyCode(source.sourceCode);
    setRunningCode(source.sourceCode);

    try {
      const readiness = await fetchFoodBranchLocatorSourceIngestionReadiness(source.sourceCode);
      const dryRun = !source.isActive;

      if (dryRun && readiness.dryRunAllowed === false) {
        showToast(
          readiness.blockingReasons?.[0] || `${source.sourceName} is not runnable yet.`,
          "error"
        );
        return;
      }

      if (!dryRun) {
        if (readiness.realRunAllowed === false) {
          showToast(
            readiness.blockingReasons?.[0] || `${source.sourceName} cannot run real import yet.`,
            "error"
          );
          return;
        }

        const confirmed = window.confirm(
          `Run branch locator import for ${source.sourceName}? This uses the default template or example row for this chain and upserts food stores.`
        );
        if (!confirmed) return;
      }

      const result = await runFoodBranchLocatorSourceIngestion(source.sourceCode, {
        dryRun,
        confirm: !dryRun,
      });

      showToast(
        getBranchLocatorRunResultMessage(result) || `Run completed for ${source.sourceName}.`,
        "success"
      );
      onRefresh?.({ silent: true });
    } catch (error) {
      showToast(parseBranchLocatorApiError(error), "error");
    } finally {
      setBusyCode(null);
      setRunningCode(null);
    }
  };

  const handleOpenImports = () => {
    window.location.hash = BRANCH_LOCATOR_IMPORTS_HREF;
  };

  if (!filteredSources.length) {
    return null;
  }

  return (
    <>
      <h4 className="admin-source-controls-dash__type-subheading">Chain branch locator sources</h4>
      <FoodSourceControlsGrid
        sources={filteredSources}
        emptyMessage="No chain branch locator sources match the current filters."
        canManageSources={canManageSources}
        busyCode={busyCode}
        runningCode={runningCode}
        onToggleEnabled={handleToggleActive}
        onRunNow={handleRunNow}
        onViewRuns={handleOpenImports}
        resolveRunNowTitle={(source) =>
          !source.isActive
            ? "Run a dry-run validation (enable source for real import)"
            : runningCode === source.sourceCode
              ? "Import run in progress"
              : "Run branch locator import now"
        }
        onRefresh={onRefresh}
        showToast={showToast}
        onSourcePatch={onSourcePatch}
      />
    </>
  );
};

export default FoodBranchLocatorSourceControlsTable;
